import { DEFAULT_TAG_COLOR } from "@/lib/tag-colors";

// Deterministic per-tag color palette for FieldPilot. Mirrors the backend palette so the
// live viewport "Color by: Tag" markers match the exported PDF markup exactly. The color for a
// tag is `palette[index % palette.length]`, where `index` is the tag's position once tags are
// sorted by `displayOrder` (the same order the rest of the FieldPilot UI uses).

export const FIELD_PILOT_TAG_PALETTE = [
  DEFAULT_TAG_COLOR,
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#14B8A6",
  "#6366F1",
  "#EAB308",
  "#10B981",
  "#F43F5E",
  "#06B6D4",
  "#A855F7",
  "#84CC16",
  "#3B82F6",
  "#D946EF",
  "#F59E0B",
  "#22C55E",
  "#EF4444",
  "#64748B",
  "#0D9488",
  "#7C3AED",
] as const;

/** The fallback color used when a tag is missing from the palette map. */
export const FIELD_PILOT_TAG_FALLBACK_COLOR = FIELD_PILOT_TAG_PALETTE[0];

/** Resolve the palette color for a 0-based position, wrapping around the palette length. */
export function tagColorForIndex(index: number): string {
  const length = FIELD_PILOT_TAG_PALETTE.length;
  if (!Number.isInteger(index) || index < 0) {
    return FIELD_PILOT_TAG_FALLBACK_COLOR;
  }
  return FIELD_PILOT_TAG_PALETTE[index % length];
}

/**
 * Build a `tagId → color` map. Tags are sorted by `displayOrder` (stable, ascending) and each is
 * assigned `palette[index % palette.length]`, so the mapping is deterministic regardless of the
 * input order. Tolerates a missing `displayOrder` (treated as 0).
 */
export function buildTagColorMap<T extends { _id: string; displayOrder?: number }>(
  tags: readonly T[]
): Map<string, string> {
  const sorted = [...tags].sort((a, b) => {
    const orderDelta = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    if (orderDelta !== 0) return orderDelta;
    // Break ties on `_id` so the mapping is stable regardless of caller order.
    return String(a._id).localeCompare(String(b._id));
  });
  const map = new Map<string, string>();
  sorted.forEach((tag, index) => {
    map.set(String(tag._id), tagColorForIndex(index));
  });
  return map;
}
